import { createVar } from "@vanilla-extract/css";

import { ResponsiveProp, isResponsiveProp } from "../Theme/ResponsiveProp";
import { ResponsiveValues } from "../Theme/ResponsiveValues";

type CSSVarFunction = ReturnType<typeof createVar>;

/**
 * Assigns CSS variables from a responsive prop.
 *
 * @param responsiveProp - The responsive prop to set CSS variables from.
 * @param cssVars - The CSS variables to set.
 * @returns A record containing the CSS variables and their corresponding values.
 */
export const assignCSSVarsFromResponsiveProp = (
  responsiveProp: ResponsiveProp<string>,
  cssVars: ResponsiveValues<CSSVarFunction>
): Record<string, string | undefined> => {
  const vars: Record<string, string | undefined> = {};
  if (isResponsiveProp(responsiveProp)) {
    vars[cssVars.mobileAndAbove] = responsiveProp.mobileAndAbove;
    if (cssVars.tabletAndAbove && responsiveProp.tabletAndAbove) {
      vars[cssVars.tabletAndAbove] = responsiveProp.tabletAndAbove;
    }
    if (cssVars.desktopAndAbove && responsiveProp.desktopAndAbove) {
      vars[cssVars.desktopAndAbove] = responsiveProp.desktopAndAbove;
    }
    if (cssVars.xLargeAndAbove && responsiveProp.xLargeAndAbove) {
      vars[cssVars.xLargeAndAbove] = responsiveProp.xLargeAndAbove;
    }
    if (cssVars.xxLargeDesktop && responsiveProp.xxLargeDesktop) {
      vars[cssVars.xxLargeDesktop] = responsiveProp.xxLargeDesktop;
    }
  } else {
    vars[cssVars.mobileAndAbove] = responsiveProp;
  }
  return vars;
};

/**
 * Retrieves the dynamic sprinkles values based on the responsiveProp and cssVars.
 * @param responsiveProp - The responsiveProp value.
 * @param cssVars - The cssVars value.
 * @returns The CSSVarFunction or ResponsiveValues<CSSVarFunction | undefined>.
 */
export const getDynamicSprinklesValues = (
  responsiveProp: ResponsiveProp<string>,
  cssVars: ResponsiveValues<CSSVarFunction>
): CSSVarFunction | ResponsiveValues<CSSVarFunction | undefined> => {
  if (isResponsiveProp(responsiveProp)) {
    return {
      mobileAndAbove:
        responsiveProp.mobileAndAbove !== undefined
          ? cssVars.mobileAndAbove
          : undefined,
      tabletAndAbove:
        responsiveProp.tabletAndAbove !== undefined
          ? cssVars.tabletAndAbove
          : undefined,
      desktopAndAbove:
        responsiveProp.desktopAndAbove !== undefined
          ? cssVars.desktopAndAbove
          : undefined,
      xLargeAndAbove:
        responsiveProp.xLargeAndAbove !== undefined
          ? cssVars.xLargeAndAbove
          : undefined,
      xxLargeDesktop:
        responsiveProp.xxLargeDesktop !== undefined
          ? cssVars.xxLargeDesktop
          : undefined,
    };
  } else {
    return cssVars.mobileAndAbove;
  }
};
